import React from 'react';
import './ErrorBoundary.css';

import { log } from '../utils/logger';
/**
 * ErrorBoundary - Componente para capturar errores de React
 * 
 * Envuelve la aplicación para capturar errores no manejados
 * y mostrar una UI elegante en lugar de pantalla en blanco
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar estado para mostrar UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Registrar el error en la consola
    log.error('ErrorBoundary capturó un error:', error, errorInfo);
    
    // Guardar información del error en el estado
    this.setState({
      error,
      errorInfo
    });

    // Aquí podrías enviar el error a un servicio de logging como Sentry
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null 
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // UI de fallback cuando hay un error
      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            {/* Icono de error */}
            <div className="error-boundary__icon">
              <svg 
                width="100" 
                height="100" 
                viewBox="0 0 100 100" 
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="50" cy="50" r="45" fill="#ff4444" opacity="0.1"/>
                <path 
                  d="M50 30 L50 60 M50 70 L50 75" 
                  stroke="#ff4444" 
                  strokeWidth="6" 
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {/* Título */}
            <h1 className="error-boundary__title">
              Oops! Algo salió mal
            </h1>

            {/* Mensaje */}
            <p className="error-boundary__message">
              Lo sentimos, ha ocurrido un error inesperado. 
              Estamos trabajando para solucionarlo.
            </p>

            {/* Detalles del error (solo en desarrollo) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary__details">
                <summary>Detalles técnicos (solo en desarrollo)</summary>
                <pre className="error-boundary__stack">
                  <strong>Error:</strong> {this.state.error.toString()}
                  {'\n\n'}
                  <strong>Stack:</strong>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Botones de acción */}
            <div className="error-boundary__actions">
              <button 
                className="btn btn--primary"
                onClick={this.handleReload}
              >
                Recargar página
              </button>
              <button 
                className="btn btn--secondary"
                onClick={this.handleReset}
              >
                Volver a intentar
              </button>
            </div>

            {/* Información de contacto */}
            <p className="error-boundary__contact">
              Si el problema persiste, por favor contacta al administrador
            </p>
          </div>
        </div>
      );
    }

    // Renderizar children normalmente si no hay error
    return this.props.children;
  }
}

export default ErrorBoundary;
